<template>
  <div class="relative" :class="{ '2xl:container': !displayOverflow && isContainer, 'py-6 lg:py-8': !noPadding }">
    <div
      v-if="heading || showArrows"
      class="slider-arrows-container lg:items-center lg:pb-6 container flex items-end justify-between pb-4"
    >
      <div
        class="slider-arrows flex flex-wrap w-full items-center"
        :class="{
          'justify-between': heading,
          'justify-start': !heading && arrowsLeft,
          'justify-end': !heading && !arrowsLeft,
        }"
      >
        <h2
          v-if="heading"
          class="slider-title font-semibold pr-4 fluid-3xl leading-tightest"
          data-test-id="heading"
          v-html="heading"
        ></h2>
        <ArrowBtnNav
          v-if="showArrows"
          class="arrow-buttons block w-full mt-5 sm:w-16 sm:mt-0"
          :class="{ hidden: isFirstSlideVisible && isLastSlideVisible, '2xl:hidden': totalItems === 3 }"
          :left-arrow-disabled="isFirstSlideVisible"
          :right-arrow-disabled="isLastSlideVisible"
          :inverted="arrowsInverted"
          data-test-id="arrowNav"
          @onLeftClick="prev"
          @onRightClick="next"
        />
      </div>
    </div>
    <div
      ref="slider"
      class="relative flex overflow-x-auto md:overflow-x-hidden overflow-y-hidden"
      :class="{
        '2xl:-mx-4 2xl:container-pl 2xl:container-pr': displayOverflow && !fullwidthSlides,
        'is-snap-enabled': isSnapEnabled,
      }"
    >
      <div ref="trackStart" class="-mr-0.5 track-start" />
      <div
        ref="track"
        class="slider-inner-grid grid grid-flow-col"
        :class="[
          {
            '2xl:px-4!': !fullwidthSlides,
            'ml-0.5': fullwidthSlides,
            'pt-4 pb-10': !noPadding,
            'min-h-[200px]': !autoHeight,
          },
          gridAutoClasses,
        ]"
        :style="sliderInnerStyle"
      >
        <slot />
      </div>
      <div ref="trackEnd" class="-ml-0.5 track-end" />
    </div>
    <div ref="sliderContainerReference" :class="[fixedWidth ? 'w-full' : 'container']" aria-hidden="true" />
  </div>
</template>

<script setup>
import ArrowBtnNav from '@/components/common/ArrowBtnNav';
import debounce from 'lodash/debounce';
import { computed, ref, watch, onMounted, onBeforeUnmount, onUpdated } from 'vue';
import { useCartStore } from '@/stores/cart';

const props = defineProps({
  heading: {
    type: String,
    default: null,
  },
  showArrows: {
    type: Boolean,
    default: true,
  },
  displayOverflow: {
    type: Boolean,
    default: false,
  },
  fullwidthSlides: {
    type: Boolean,
    default: false,
  },
  isContainer: {
    type: Boolean,
    default: true,
  },
  arrowsInverted: {
    type: Boolean,
    default: false,
  },
  arrowsLeft: {
    type: Boolean,
    default: false,
  },
  slideGap: {
    type: Number,
    default: 16,
  },
  slideLeveling: {
    type: Boolean,
    default: true,
  },
  noPadding: {
    type: Boolean,
    default: false,
  },
  fixedWidth: {
    type: Number,
    default: null,
  },
  autoHeight: {
    type: Boolean,
    default: false,
  },
  breakpoints: {
    type: Array,
    default: () => [
      {
        minWindowWidth: 0,
        slidesToShow: 2,
        slidesToScroll: 1,
      },
      {
        minWindowWidth: 700,
        slidesToShow: 2,
        slidesToScroll: 2,
      },
      {
        minWindowWidth: 1000,
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    ],
  },
  doAutomaticallyUpdateActiveSlide: {
    type: Boolean,
    default: true,
  },
  numberOfItems: {
    type: Array,
    default: undefined,
  },
});

const emit = defineEmits(['change']);

const isFirstSlideVisible = ref(true);
const isLastSlideVisible = ref(false);
const containerWidth = ref(null);
const windowWidth = ref(null);
const isSlidingByClick = ref(false);
const isSnapEnabled = ref(false);
const activeSlide = ref(0);
const slider = ref(null);
const track = ref(null);
const trackStart = ref(null);
const trackEnd = ref(null);
const sliderContainerReference = ref(null);
const observer = ref(null);

const totalItems = computed(() => props.numberOfItems?.length || 0);

const cartStore = useCartStore();
const isOpen = computed(() => cartStore.isOpen);

const gutterWidth = computed(() => {
  if (props.fullwidthSlides) return 1;
  if (containerWidth.value * 0.025 > props.slideGap) {
    return containerWidth.value * 0.025;
  } else {
    return props.slideGap;
  }
});

const currentBreakpoint = computed(() => {
  return [...props.breakpoints].filter((item) => windowWidth.value >= item.minWindowWidth).pop();
});

const slidesToScroll = computed(() => currentBreakpoint.value?.slidesToScroll);

const slideWidth = computed(() => {
  if (props.fixedWidth) {
    return props.fixedWidth;
  }
  if (currentBreakpoint.value?.slidesToShow) {
    if (props.fullwidthSlides) {
      return slider.value?.getBoundingClientRect()?.width / currentBreakpoint.value?.slidesToShow;
    } else {
      return (
        (containerWidth.value - gutterWidth.value * currentBreakpoint.value?.slidesToShow) /
        currentBreakpoint.value?.slidesToShow
      );
    }
  } else {
    return sliderContainerReference.value?.getBoundingClientRect()?.width;
  }
});

const currentContainerWidth = computed(() => containerWidth.value);

const columnSize = computed(() => {
  if (!props.fullwidthSlides && currentContainerWidth.value && slideWidth.value > currentContainerWidth.value) {
    return currentContainerWidth.value;
  } else {
    return slideWidth.value;
  }
});

const gridAutoClasses = computed(() => {
  const lowestSlidesToShow = Math.trunc(10 / props.breakpoints[0].slidesToShow) * 10;
  const highestSlidesToShow = Math.trunc(10 / props.breakpoints[props.breakpoints.length - 1].slidesToShow) * 10;
  return `auto-cols-[minmax(${lowestSlidesToShow}vw,auto)] md:auto-cols-[minmax(${highestSlidesToShow}vw,auto)]`;
});

const sliderInnerStyle = computed(() => {
  const styles = {
    gridColumnGap: gutterWidth.value + 'px',
    gridAutoColumns: columnSize.value && `${columnSize.value}px`,
  };
  if (!props.noPadding) {
    styles.paddingLeft = gutterWidth.value / 2 + 'px';
    styles.paddingRight = gutterWidth.value / 2 + 'px';
  }
  return styles;
});

watch(
  () => [isFirstSlideVisible.value, isLastSlideVisible.value],
  () => {
    emitSlideChange();
  }
);

watch(isOpen, () => {
  // Use nextTick to ensure the DOM is updated before setLayout is called
  nextTick(() => {
    setLayout();
  });
});

const emitSlideChange = () => {
  emit('change', {
    currentSlide: activeSlide.value,
    isFirst: isFirstSlideVisible.value,
    isLast: isLastSlideVisible.value,
  });
};

const manuallyUpdateActiveSlide = (newActiveSlideIndex) => {
  activeSlide.value = newActiveSlideIndex;
  emitSlideChange();
};

const automaticallyUpdateActiveSlide = () => {
  if (!slider.value) return;

  const sliderOffset = slider.value.scrollLeft;
  const newActiveSlide = Math.round(sliderOffset / (slideWidth.value + gutterWidth.value));
  if (newActiveSlide !== activeSlide.value) {
    activeSlide.value = newActiveSlide;
    emitSlideChange();
  }
};

const debounceSlidingByClick = debounce(() => {
  isSlidingByClick.value = false;
}, 1000);

const debounceUpdateActiveSlide = debounce(() => {
  if (props.doAutomaticallyUpdateActiveSlide) {
    automaticallyUpdateActiveSlide();
    if (props.slideLeveling) {
      scrollToSlide(activeSlide.value);
    }
  }
}, 300);

const handleScroll = () => {
  if (!isSlidingByClick.value && !isSnapEnabled.value) isSnapEnabled.value = true;
};

const handleWindowResize = () => {
  setLayout();
  windowWidth.value = window.innerWidth;
};

const setLayout = () => {
  const containerRect = sliderContainerReference.value?.getBoundingClientRect();
  containerWidth.value = containerRect?.width;
};

const scrollSlides = (slidesCount) => {
  isSlidingByClick.value = true;
  isSnapEnabled.value = false;

  nextTick(() => {
    debounceSlidingByClick();

    if (!slider.value) {
      return;
    }

    let destination;
    if (slidesCount !== 0) {
      destination = slider.value.scrollLeft + slidesCount * slideWidth.value + slidesCount * gutterWidth.value;
    } else {
      destination = activeSlide.value * slideWidth.value + activeSlide.value * gutterWidth.value;
    }

    slider.value.scrollTo({
      left: destination,
      behavior: 'smooth',
    });
  });

  emitSlideChange();
};

const scrollToSlide = (slideIndex) => {
  manuallyUpdateActiveSlide(slideIndex);
  scrollSlides(slideIndex - activeSlide.value);
};

const prev = () => {
  if (activeSlide.value > 0) {
    manuallyUpdateActiveSlide(activeSlide.value - 1);
    scrollSlides(-slidesToScroll.value);
  } else {
    scrollSlides(0);
  }
};

const next = () => {
  const numberOfSlides = track.value.childElementCount - 1;
  if (activeSlide.value < numberOfSlides) {
    manuallyUpdateActiveSlide(activeSlide.value + 1);
    scrollSlides(slidesToScroll.value);
  }
};

const mountObserver = () => {
  if (window?.IntersectionObserver) {
    observer.value = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target.classList.contains('track-start')) {
            isFirstSlideVisible.value = entry.intersectionRatio === 1;
          }
          if (entry.target.classList.contains('track-end')) {
            isLastSlideVisible.value = entry.intersectionRatio === 1;
          }
        });
      },
      { root: slider.value, threshold: [0, 1] }
    );

    observer.value.observe(trackStart.value);
    observer.value.observe(trackEnd.value);
  }
};

onMounted(() => {
  windowWidth.value = window.innerWidth;
  slider.value.addEventListener('scroll', debounceUpdateActiveSlide);
  window.addEventListener('resize', handleWindowResize);
  slider.value.addEventListener('scroll', handleScroll);
  setLayout();
  emitSlideChange();
});

onUpdated(() => {
  nextTick(() => {
    if (!observer.value && track.value?.childElementCount > 0) mountObserver();
  });
});

onBeforeUnmount(() => {
  observer.value?.disconnect();
  window.removeEventListener('resize', handleWindowResize);
  slider.value?.removeEventListener('scroll', debounceUpdateActiveSlide);
  slider.value?.removeEventListener('scroll', handleScroll);
});

defineExpose({
  prev,
  next,
  scrollToSlide,
});
</script>

<style scoped>
@reference "~/assets/css/main.css";
.slider-root {
  scroll-padding-left: 6vw;
}

@media (width >= 1024px) {
  .slider-root {
    scroll-padding-left: unset;
  }
}

.slider-root.is-snap-enabled {
  scroll-snap-type: x mandatory;
}
.slider-root::-webkit-scrollbar {
  @apply hidden;
}
.slider-inner-grid {
  @apply grid-flow-col;
}

.slider-inner-grid > * {
  scroll-snap-align: start;
}

@media (width >= 1024px) {
  .slider-inner-grid > * {
    scroll-snap-align: center;
  }
}
</style>
